import { useRef, useState, useEffect } from 'react';
import { motion, useInView, animate } from 'framer-motion';
import { Zap, Cpu, Globe } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';
import translations from '@/utils/translations';

function AnimatedCounter({ value, suffix, decimals = 0 }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    if (isInView && value > 0) {
      const controls = animate(0, value, {
        duration: 2,
        ease: 'easeOut',
        onUpdate: (v) =>
          setDisplay(
            decimals > 0 ? parseFloat(v.toFixed(decimals)) : Math.round(v)
          ),
      });
      return controls.stop;
    }
  }, [isInView, value, decimals]);

  return (
    <span ref={ref} className="font-heading text-4xl xl:text-[2.8rem] font-bold text-[#ffa600]">
      {value > 0 ? display : ''}
      {suffix}
    </span>
  );
}

const containerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.12 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 25 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

function WhyUsIcon({ icon }) {
  if (icon === 'tech') {
    return <Cpu className="w-7 h-7 text-[#ffa600]" />;
  }

  if (icon === 'languages') {
    return <Globe className="w-7 h-7 text-[#ffa600]" />;
  }

  return <Zap className="w-7 h-7 text-[#ffa600]" />;
}

export default function WhyUs() {
  const { language } = useLanguage();
  const t = translations[language].whyUs;

  return (
    <section className="py-20 md:py-32 bg-[#e3e6e8]" data-testid="whyus-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.6 }}
          className="text-center mb-14"
        >
          <h2 className="font-heading text-3xl md:text-4xl font-semibold text-[#5b6770]">
            {t.title}
          </h2>
          <p className="text-sm text-[#5b6770] mt-3 lg:hidden flex items-center justify-center gap-2">
            <span>Desliza para ver más</span>
            <span aria-hidden="true">→</span>
          </p>
        </motion.div>

        <div className="relative lg:hidden">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-60px' }}
            className="flex gap-6 overflow-x-auto pb-4 snap-x snap-mandatory scroll-smooth [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
          >
            {t.items.map((item, i) => (
              <motion.div
                key={i}
                variants={itemVariants}
                className="snap-start shrink-0 w-[78%] sm:w-[44%] bg-white rounded-2xl p-7 text-center border border-gray-100"
                data-testid={`whyus-item-mobile-${i}`}
              >
                {item.isIcon ? (
                  <div className="w-14 h-14 bg-[#fff6e5] rounded-2xl flex items-center justify-center mx-auto mb-3">
                    <WhyUsIcon icon={item.icon} />
                  </div>
                ) : (
                  <div className="mb-3">
                    <AnimatedCounter
                      value={item.value}
                      suffix={item.suffix}
                      decimals={item.suffix === '' && item.value % 1 !== 0 ? 1 : 0}
                    />
                  </div>
                )}

                <h3 className="text-base font-semibold text-[#5b6770] mb-2">
                  {item.label}
                </h3>

                <p className="text-[#5b6770] text-sm leading-relaxed">
                  {item.desc}
                </p>
              </motion.div>
            ))}
          </motion.div>

          <div className="pointer-events-none absolute inset-y-0 left-0 w-8 bg-gradient-to-r from-[#e3e6e8] to-transparent" />
          <div className="pointer-events-none absolute inset-y-0 right-0 w-8 bg-gradient-to-l from-[#e3e6e8] to-transparent" />
        </div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-60px' }}
          className="hidden xl:grid xl:grid-cols-5 gap-5"
        >
          {t.items.map((item, i) => (
            <motion.div
              key={i}
              variants={itemVariants}
              className="bg-white rounded-2xl p-6 text-center hover:-translate-y-1 hover:shadow-[0_8px_30px_rgba(0,0,0,0.06)] transition-all duration-300 border border-gray-100 min-h-[240px] flex flex-col items-center justify-center"
              data-testid={`whyus-item-desktop-${i}`}
            >
              {item.isIcon ? (
                <div className="w-14 h-14 bg-[#fff6e5] rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <WhyUsIcon icon={item.icon} />
                </div>
              ) : (
                <div className="mb-4">
                  <AnimatedCounter
                    value={item.value}
                    suffix={item.suffix}
                    decimals={item.suffix === '' && item.value % 1 !== 0 ? 1 : 0}
                  />
                </div>
              )}

              <h3 className="text-[1.05rem] font-semibold text-[#5b6770] mb-3">
                {item.label}
              </h3>

              <p className="text-[#5b6770] text-sm leading-relaxed">
                {item.desc}
              </p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}